import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { agencyApi } from '@/api/services/agency.api';
import { ORGANIZATION_QUERY_KEYS } from '@/features/organization/constants/organization.constants';

import { IS_AGENCY_MODULE_ENABLED } from '../feature-flag';
import { AGENCY_QUERY_KEYS } from '../query-keys';
import type {
    IAgencyPlanPricingRule,
    IUpdateBrandingInput,
    ICreateClientInput,
    ICreateSetupInput,
    IUpdateClientPricingInput,
} from '../types';

export function useMyAgency() {
    return useQuery({
        queryKey: AGENCY_QUERY_KEYS.me(),
        queryFn: agencyApi.getMe,
        staleTime: 5 * 60 * 1000,
        // The sidebar and the org switcher both call this on every app mount,
        // outside their own `IS_AGENCY_MODULE_ENABLED` checks. With the console
        // withdrawn nothing renders from the result, so the request is pure
        // waste — and an agency call still on the wire reads like the module is
        // only half-withdrawn. Gated on the same flag, so the agency pages get
        // their data back the moment the console returns.
        enabled: IS_AGENCY_MODULE_ENABLED,
    });
}

export function useCreateAgency() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (input: { name: string }) => agencyApi.create(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.all });
            toast.success(t('agency.toasts.agencyCreated'));
        },
        onError: () => {
            toast.error(t('agency.toasts.agencyCreateFailed'));
        },
    });
}

export function useAgencyOverview(enabled = true) {
    return useQuery({
        queryKey: AGENCY_QUERY_KEYS.overview(),
        queryFn: agencyApi.getOverview,
        staleTime: 30_000,
        // Aggregates live billing-runtime reads — don't hammer it when down.
        retry: 1,
        enabled,
    });
}

export function useAgencyClients(enabled = true) {
    return useQuery({
        queryKey: AGENCY_QUERY_KEYS.clients(),
        queryFn: agencyApi.getClients,
        staleTime: 30_000,
        retry: 1,
        enabled,
    });
}

export function useCreateClient() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (input: ICreateClientInput) => agencyApi.createClient(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.all });
            // The creator now owns a new workspace — the switcher list changed.
            void queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.all });
            toast.success(t('agency.toasts.clientCreated'));
        },
        onError: () => {
            toast.error(t('agency.toasts.clientCreateFailed'));
        },
    });
}

export function useAgencyPricing(enabled = true) {
    return useQuery({
        queryKey: AGENCY_QUERY_KEYS.pricing(),
        queryFn: agencyApi.getPricing,
        staleTime: 60_000,
        enabled,
    });
}

export function useUpdateAgencyPricing() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (rules: readonly IAgencyPlanPricingRule[]) => agencyApi.updatePricing(rules),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.all });
            toast.success(t('agency.toasts.pricingSaved'));
        },
        onError: () => {
            toast.error(t('agency.toasts.pricingSaveFailed'));
        },
    });
}

export function useUpdateClientPricing() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (vars: { organizationId: string; input: IUpdateClientPricingInput }) =>
            agencyApi.updateClientPricing(vars.organizationId, vars.input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.all });
            toast.success(t('agency.toasts.pricingSaved'));
        },
        onError: () => {
            toast.error(t('agency.toasts.pricingSaveFailed'));
        },
    });
}

export function useAgencySetups(enabled = true) {
    return useQuery({
        queryKey: AGENCY_QUERY_KEYS.setups(),
        queryFn: agencyApi.getSetups,
        staleTime: 60_000,
        enabled,
    });
}

export function useCreateSetup() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (input: ICreateSetupInput) => agencyApi.createSetup(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.setups() });
            toast.success(t('agency.toasts.setupSaved'));
        },
        onError: () => {
            toast.error(t('agency.toasts.setupSaveFailed'));
        },
    });
}

export function useDuplicateSetup() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (setupId: string) => agencyApi.duplicateSetup(setupId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.setups() });
            toast.success(t('agency.toasts.setupDuplicated'));
        },
        onError: () => {
            toast.error(t('agency.toasts.setupActionFailed'));
        },
    });
}

export function useDeleteSetup() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (setupId: string) => agencyApi.deleteSetup(setupId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.setups() });
            toast.success(t('agency.toasts.setupDeleted'));
        },
        onError: () => {
            toast.error(t('agency.toasts.setupActionFailed'));
        },
    });
}

export function useInstallSetup() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (vars: { organizationId: string; setupId: string }) =>
            agencyApi.installSetup(vars.organizationId, vars.setupId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.all });
            toast.success(t('agency.toasts.setupInstalled'));
        },
        onError: () => {
            toast.error(t('agency.toasts.setupActionFailed'));
        },
    });
}

export function useAgencyBranding(enabled = true) {
    return useQuery({
        queryKey: AGENCY_QUERY_KEYS.branding(),
        queryFn: agencyApi.getBranding,
        staleTime: 60_000,
        enabled,
    });
}

export function useUpdateAgencyBranding() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (input: IUpdateBrandingInput) => agencyApi.updateBranding(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.branding() });
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.resolvedBranding() });
            toast.success(t('agency.toasts.brandingSaved'));
        },
        onError: () => {
            toast.error(t('agency.toasts.brandingSaveFailed'));
        },
    });
}

export function useUploadBrandingImage() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (vars: { kind: 'logo' | 'favicon'; file: File }) =>
            agencyApi.uploadBrandingImage(vars.kind, vars.file),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.branding() });
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.resolvedBranding() });
            toast.success(t('agency.toasts.brandingSaved'));
        },
        onError: () => {
            toast.error(t('agency.toasts.brandingSaveFailed'));
        },
    });
}

export function useBrandingDomain() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.branding() });
    };

    const set = useMutation({
        mutationFn: (domain: string) => agencyApi.setBrandingDomain(domain),
        onSuccess: invalidate,
        onError: () => toast.error(t('agency.toasts.brandingSaveFailed')),
    });
    const verify = useMutation({
        mutationFn: () => agencyApi.verifyBrandingDomain(),
        onSuccess: (branding) => {
            invalidate();
            if (branding.domainStatus === 'VERIFIED') {
                toast.success(t('agency.toasts.domainConnected'));
            } else {
                toast.error(t('agency.toasts.domainNotFound'));
            }
        },
        onError: () => toast.error(t('agency.toasts.brandingSaveFailed')),
    });
    const remove = useMutation({
        mutationFn: () => agencyApi.removeBrandingDomain(),
        onSuccess: invalidate,
        onError: () => toast.error(t('agency.toasts.brandingSaveFailed')),
    });
    return { set, verify, remove };
}

/** §51-resolved branding for the current workspace — safe for every member. */
export function useResolvedBranding() {
    return useQuery({
        queryKey: AGENCY_QUERY_KEYS.resolvedBranding(),
        queryFn: agencyApi.getResolvedBranding,
        staleTime: 5 * 60 * 1000,
    });
}

export function useShareSetup() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (setupId: string) => agencyApi.shareSetup(setupId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.setups() });
        },
        onError: () => {
            toast.error(t('agency.toasts.setupActionFailed'));
        },
    });
}

export function useRevokeSetupShare() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (setupId: string) => agencyApi.revokeSetupShare(setupId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.setups() });
            toast.success(t('agency.toasts.shareRevoked'));
        },
        onError: () => {
            toast.error(t('agency.toasts.setupActionFailed'));
        },
    });
}

export function useSharedSetupPreview(code: string) {
    return useQuery({
        queryKey: [...AGENCY_QUERY_KEYS.setups(), 'shared', code],
        queryFn: () => agencyApi.getSharedSetupPreview(code),
        enabled: code.trim().length >= 10,
        retry: false,
        staleTime: 60_000,
    });
}

export function useImportSharedSetup() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (code: string) => agencyApi.importSharedSetup(code),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.setups() });
            toast.success(t('agency.toasts.setupImported'));
        },
        onError: () => {
            toast.error(t('agency.toasts.setupImportFailed'));
        },
    });
}

export function useSetupUpdatePreview(organizationId: string | null, setupId: string | null) {
    return useQuery({
        queryKey: [...AGENCY_QUERY_KEYS.setups(), 'update-preview', organizationId, setupId],
        queryFn: () => agencyApi.getSetupUpdatePreview(organizationId!, setupId!),
        enabled: !!organizationId && !!setupId,
        staleTime: 30_000,
    });
}

export function useAgencyActivity(enabled = true) {
    return useQuery({
        queryKey: [...AGENCY_QUERY_KEYS.all, 'activity'],
        queryFn: () => agencyApi.getActivity(),
        staleTime: 30_000,
        enabled,
    });
}

export function useUpdateAgencySettings() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (input: { defaultMarginPct: number | null }) =>
            agencyApi.updateAgencySettings(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.me() });
            toast.success(t('agency.toasts.settingsSaved', 'Saved'));
        },
        onError: () => {
            toast.error(t('agency.toasts.settingsSaveFailed', "That didn't save. Try again."));
        },
    });
}

export function useClientOnboarding(organizationId: string | null) {
    return useQuery({
        queryKey: [...AGENCY_QUERY_KEYS.clients(), organizationId, 'onboarding'],
        queryFn: () => agencyApi.getClientOnboarding(organizationId!),
        enabled: !!organizationId,
        staleTime: 15_000,
    });
}

export function useToggleOnboardingItem() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (vars: { organizationId: string; itemKey: string }) =>
            agencyApi.toggleOnboardingItem(vars.organizationId, vars.itemKey),
        onSuccess: (_data, vars) => {
            void queryClient.invalidateQueries({
                queryKey: [...AGENCY_QUERY_KEYS.clients(), vars.organizationId, 'onboarding'],
            });
        },
        onError: () => {
            toast.error(t('agency.toasts.setupActionFailed'));
        },
    });
}

export function useStripeConnection(enabled = true) {
    return useQuery({
        queryKey: [...AGENCY_QUERY_KEYS.all, 'stripe'],
        queryFn: agencyApi.getStripeConnection,
        staleTime: 60_000,
        enabled,
    });
}

export function useConnectStripe() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (secretKey: string) => agencyApi.connectStripe(secretKey),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [...AGENCY_QUERY_KEYS.all, 'stripe'] });
            toast.success(t('agency.toasts.stripeConnected', 'Stripe connected'));
        },
        onError: (error) => {
            const message =
                (error as { response?: { data?: { error?: { message?: string } } } }).response?.data
                    ?.error?.message ??
                t('agency.toasts.stripeConnectFailed', "We couldn't connect that Stripe account.");
            toast.error(message);
        },
    });
}

export function useDisconnectStripe() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: agencyApi.disconnectStripe,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [...AGENCY_QUERY_KEYS.all, 'stripe'] });
        },
    });
}

export function useClientRebill(organizationId: string | null) {
    return useQuery({
        queryKey: [...AGENCY_QUERY_KEYS.clients(), organizationId, 'rebill'],
        queryFn: () => agencyApi.getClientRebill(organizationId!),
        enabled: !!organizationId,
        staleTime: 30_000,
    });
}

export function useStartClientBilling() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (organizationId: string) => agencyApi.startClientBilling(organizationId),
        onSuccess: (_data, organizationId) => {
            void queryClient.invalidateQueries({
                queryKey: [...AGENCY_QUERY_KEYS.clients(), organizationId, 'rebill'],
            });
            toast.success(t('agency.toasts.billingStarted', 'Billing started'));
        },
        onError: (error) => {
            const message =
                (error as { response?: { data?: { error?: { message?: string } } } }).response?.data
                    ?.error?.message ??
                t('agency.toasts.billingActionFailed', "That didn't work. Please try again.");
            toast.error(message);
        },
    });
}

export function useStopClientBilling() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (organizationId: string) => agencyApi.stopClientBilling(organizationId),
        onSuccess: (_data, organizationId) => {
            void queryClient.invalidateQueries({
                queryKey: [...AGENCY_QUERY_KEYS.clients(), organizationId, 'rebill'],
            });
            toast.success(t('agency.toasts.billingStopped', 'Billing stopped'));
        },
        onError: () => {
            toast.error(
                t('agency.toasts.billingActionFailed', "That didn't work. Please try again."),
            );
        },
    });
}

export function useUpdateSetup() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (vars: {
            setupId: string;
            input: { name?: string; description?: string | null; industry?: string | null };
        }) => agencyApi.updateSetup(vars.setupId, vars.input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.setups() });
            toast.success(t('agency.toasts.setupUpdated', 'Setup updated'));
        },
        onError: () => {
            toast.error(t('agency.toasts.setupActionFailed'));
        },
    });
}
